/**
 * 📊 Data Tracker for Main-Bet Toast Notifications
 * 
 * Tracks user interactions and triggers relevant notifications
 * Integrates with toastNotifications system
 */

import toastNotifications from './toastNotifications';

// Конфигурация трекера
const CONFIG = {
  MAX_VIEWED_MATCHES: 10,
  ACCUMULATOR_PROMPT_DELAYS: [2000, 3000], // 2-3 секунды после просмотра
  ACCUMULATOR_TRIGGER_COUNTS: [3, 5], // показывать после 3 или 5 матчей
  ODDS_SIMULATION: {
    MIN_CHANGE: 0.1,
    MAX_CHANGE: 0.4,
    PROBABILITY: 0.3 // 30% вероятность в продакшне
  },
  LOCAL_STORAGE_KEY: 'mainbet_user_data',
  IS_DEVELOPMENT: process.env.NODE_ENV === 'development'
};

// Безопасная работа с localStorage
const safeLocalStorage = {
  getItem: (key) => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        return localStorage.getItem(key);
      }
    } catch (error) {
      if (CONFIG.IS_DEVELOPMENT) console.warn('localStorage getItem failed:', error);
    }
    return null;
  },
  
  setItem: (key, value) => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(key, value);
        return true;
      }
    } catch (error) {
      if (CONFIG.IS_DEVELOPMENT) console.warn('localStorage setItem failed:', error);
    }
    return false;
  }
};

class MainBetDataTracker {
  constructor() {
    this.data = {
      todayMatches: [],
      viewedMatches: [],
      availableBonuses: [],
      userSession: {
        startTime: Date.now(),
        pageViews: 0,
        matchViews: 0
      }
    };
    
    this.isInitialized = false;
  }

  // Инициализация трекера
  initialize() {
    if (this.isInitialized) return;
    
    this.isInitialized = true;
    this.loadStoredData();
    this.setupGlobalDataAccess();
    
    if (CONFIG.IS_DEVELOPMENT) {
      console.log('📊 Data tracker initialized');
    }
  }

  // Загрузка сохраненных данных
  loadStoredData() {
    const stored = safeLocalStorage.getItem(CONFIG.LOCAL_STORAGE_KEY);
    if (stored) {
      try {
        const parsedData = JSON.parse(stored);
        // Сохраняем только данные текущей сессии для viewedMatches
        this.data.viewedMatches = Array.isArray(parsedData.viewedMatches) 
          ? parsedData.viewedMatches 
          : [];
      } catch (error) {
        if (CONFIG.IS_DEVELOPMENT) console.warn('Could not load stored user data:', error);
      }
    }
  }

  // Сохранение данных
  saveData() {
    const dataToSave = {
      viewedMatches: this.data.viewedMatches,
      lastSaved: Date.now()
    };
    
    safeLocalStorage.setItem(CONFIG.LOCAL_STORAGE_KEY, JSON.stringify(dataToSave));
  }

  // Делаем данные доступными глобально для toast системы
  setupGlobalDataAccess() {
    if (typeof window !== 'undefined') {
      window.mainBetData = this.data;
    }
  }

  // === ТРЕКИНГ МЕТОДЫ ===

  // Установка сегодняшних матчей
  setTodayMatches(matches) {
    this.data.todayMatches = Array.isArray(matches) ? matches : [];
    this.setupGlobalDataAccess();
  }

  // Трекинг просмотра матча
  trackMatchView(match) {
    if (!match || !match.slug) return;

    // Избегаем дубликатов
    const alreadyViewed = this.data.viewedMatches.find(m => m.slug === match.slug);
    if (alreadyViewed) return;

    // Добавляем к просмотренным
    this.data.viewedMatches.push({
      slug: match.slug,
      homeTeam: match.homeTeam,
      awayTeam: match.awayTeam,
      viewedAt: Date.now()
    });

    // Ограничиваем историю последними матчами
    if (this.data.viewedMatches.length > CONFIG.MAX_VIEWED_MATCHES) {
      this.data.viewedMatches = this.data.viewedMatches.slice(-CONFIG.MAX_VIEWED_MATCHES);
    }

    this.data.userSession.matchViews++;
    this.setupGlobalDataAccess();
    this.saveData();

    // Проверяем, нужно ли показать призыв к аккумулятору
    this.checkAccumulatorPrompt();
  }

  // Трекинг просмотра страницы
  trackPageView(path) {
    this.data.userSession.pageViews++;
    this.setupGlobalDataAccess();
  }

  // Установка доступных бонусов
  setAvailableBonuses(bonuses) {
    this.data.availableBonuses = Array.isArray(bonuses) ? bonuses : [];
    this.setupGlobalDataAccess();
  }

  // Уведомление о новом бонусе
  notifyNewBonus(bonus) {
    if (!bonus || !bonus.title) return;
    toastNotifications.showNewBonus(bonus);
  }

  // === ЛОГИКА УВЕДОМЛЕНИЙ ===

  // Проверка необходимости показа призыва к аккумулятору
  checkAccumulatorPrompt() {
    const viewedCount = this.data.viewedMatches.length;
    
    // Показываем после просмотра определенного количества матчей
    if (CONFIG.ACCUMULATOR_TRIGGER_COUNTS.includes(viewedCount)) {
      const delay = CONFIG.ACCUMULATOR_PROMPT_DELAYS[
        Math.floor(Math.random() * CONFIG.ACCUMULATOR_PROMPT_DELAYS.length)
      ];
      
      setTimeout(() => {
        toastNotifications.showAccumulatorPrompt();
      }, delay);
    }
  }

  // Симуляция изменения коэффициентов (для демо и тестирования)
  simulateOddsChange(match) {
    if (!match || !match.odds) return;

    // В продакшне используем реалистичную вероятность
    const shouldSimulate = CONFIG.IS_DEVELOPMENT ? true : 
      Math.random() < CONFIG.ODDS_SIMULATION.PROBABILITY;
    
    if (!shouldSimulate) return;

    const oldOdds = { ...match.odds };
    
    // Случайное изменение коэффициентов в реалистичном диапазоне
    const changeRange = CONFIG.ODDS_SIMULATION.MAX_CHANGE - CONFIG.ODDS_SIMULATION.MIN_CHANGE;
    const change = (Math.random() - 0.5) * changeRange; 
    
    const newOdds = {
      home: Math.max(1.1, +(oldOdds.home + change).toFixed(2)),
      away: Math.max(1.1, +(oldOdds.away + change).toFixed(2)),
      draw: Math.max(1.1, +(oldOdds.draw + change).toFixed(2))
    };

    // Показываем уведомление только если изменение значимое
    if (Math.abs(change) >= CONFIG.ODDS_SIMULATION.MIN_CHANGE) {
      toastNotifications.showOddsChange(match, oldOdds, newOdds);
    }
  }

  // === ВСПОМОГАТЕЛЬНЫЕ МЕТОДЫ ===

  // Получение статистики пользователя
  getUserStats() {
    return {
      ...this.data.userSession,
      viewedMatchesCount: this.data.viewedMatches.length,
      sessionDuration: Date.now() - this.data.userSession.startTime
    };
  }

  // Очистка данных сессии
  clearSessionData() {
    this.data.viewedMatches = [];
    this.data.userSession = {
      startTime: Date.now(),
      pageViews: 0,
      matchViews: 0
    };
    this.saveData();
    this.setupGlobalDataAccess();
  }

  // Получение рекомендаций для аккумулятора
  getAccumulatorRecommendations() {
    // Возвращаем последние просмотренные матчи
    return this.data.viewedMatches.slice(-5);
  }
  
  // Получение всех данных (для отладки в dev режиме)
  getAllData() {
    return CONFIG.IS_DEVELOPMENT ? this.data : null;
  }
  
  // Получение просмотренных матчей
  getViewedMatches() {
    return this.data.viewedMatches;
  }
}

// Глобальный экземпляр трекера
export const dataTracker = new MainBetDataTracker();

// Удобные методы для экспорта
const dataTrackerAPI = {
  initialize: () => dataTracker.initialize(),
  setTodayMatches: (matches) => dataTracker.setTodayMatches(matches),
  trackMatchView: (match) => dataTracker.trackMatchView(match),
  trackPageView: (path) => dataTracker.trackPageView(path),
  setAvailableBonuses: (bonuses) => dataTracker.setAvailableBonuses(bonuses),
  notifyNewBonus: (bonus) => dataTracker.notifyNewBonus(bonus),
  simulateOddsChange: (match) => dataTracker.simulateOddsChange(match),
  getUserStats: () => dataTracker.getUserStats(),
  clearSessionData: () => dataTracker.clearSessionData(),
  getAccumulatorRecommendations: () => dataTracker.getAccumulatorRecommendations(),
  getAllData: () => dataTracker.getAllData(),
  getViewedMatches: () => dataTracker.getViewedMatches()
};

export default dataTrackerAPI; 